import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import * as XLSX from 'xlsx';

const Surveys = () => {
    const [questions, setQuestions] = useState([]);
    const [results, setResults] = useState([]);
    const [speakers, setSpeakers] = useState([]);
    const [activeTab, setActiveTab] = useState('questions'); // 'questions' or 'results'
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [formData, setFormData] = useState({
        text: '',
        type: 'text',
        options: [],
        required: true,
        sort_order: 0,
        status: 'active',
        is_mixed: false,
        sub_question_text: '',
        speaker_id: null,
        correct_answer: ''
    });
    const [newOption, setNewOption] = useState('');

    useEffect(() => {
        fetchQuestions();
        fetchResults();
        fetchSpeakers();
    }, []);

    const fetchSpeakers = async () => {
        try {
            const res = await api.get('/speakers');
            setSpeakers(res.data);
        } catch (err) {
            console.error("Error fetching speakers", err);
        }
    };

    const fetchQuestions = async () => {
        try {
            const res = await api.get('/surveys/admin/questions');
            setQuestions(res.data);
        } catch (err) {
            console.error("Error fetching survey questions", err);
        }
    };

    const fetchResults = async () => {
        try {
            const res = await api.get('/surveys/results');
            setResults(res.data);
        } catch (err) {
            console.error("Error fetching survey results", err);
        }
    };

    const handleSaveQuestion = async (e) => {
        e.preventDefault();
        try {
            if (editingQuestion) {
                await api.put(`/surveys/admin/questions/${editingQuestion.id}`, formData);
            } else {
                await api.post('/surveys/admin/questions', formData);
            }
            setIsModalOpen(false);
            setEditingQuestion(null);
            fetchQuestions();
        } catch (err) {
            console.error("Error saving question", err);
        }
    };

    const handleDeleteQuestion = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar esta pregunta?')) return;
        try {
            await api.delete(`/surveys/admin/questions/${id}`);
            fetchQuestions();
        } catch (err) {
            console.error("Error deleting question", err);
        }
    };

    const openModal = (q = null) => {
        if (q) {
            setEditingQuestion(q);
            setFormData({
                text: q.text,
                type: q.type,
                options: typeof q.options === 'string' ? JSON.parse(q.options) : (q.options || []),
                required: q.required === 1,
                sort_order: q.sort_order,
                status: q.status,
                is_mixed: q.is_mixed === 1,
                sub_question_text: q.sub_question_text || '',
                speaker_id: q.speaker_id || null,
                correct_answer: q.correct_answer || ''
            });
        } else {
            setEditingQuestion(null);
            setFormData({
                text: '',
                type: 'text',
                options: [],
                required: true,
                sort_order: questions.length + 1,
                status: 'active',
                is_mixed: false,
                sub_question_text: '',
                speaker_id: null,
                correct_answer: ''
            });
        }
        setIsModalOpen(true);
    };

    const addOption = () => {
        if (!newOption.trim()) return;
        setFormData({ ...formData, options: [...formData.options, newOption.trim()] });
        setNewOption('');
    };

    const removeOption = (idx) => {
        setFormData({ ...formData, options: formData.options.filter((_, i) => i !== idx) });
    };

    const handleExportExcel = () => {
        if (results.length === 0) {
            alert("No hay resultados para exportar");
            return;
        }

        // 1. Data for Detailed Sheet
        const dataDetailed = results.map(r => ({
            'Fecha': new Date(r.created_at).toLocaleString(),
            'ID Anónimo': r.anonymous_id,
            'Pregunta': r.question_text || 'General',
            'Respuesta': r.answer,
            'Puntaje (Rating)': r.rating || ''
        }));

        // 2. Data for Summary Sheet (Totals by Question)
        const summaryMap = {};
        results.forEach(r => {
            const qText = r.question_text || 'General';
            if (!summaryMap[qText]) {
                summaryMap[qText] = { 
                    'Pregunta': qText, 
                    'Total Votos': 0, 
                    'Suma Puntaje': 0,
                    'Promedio': 0
                };
            }
            summaryMap[qText]['Total Votos'] += 1;
            if (r.rating) {
                summaryMap[qText]['Suma Puntaje'] += r.rating;
            }
        });

        const dataSummary = Object.values(summaryMap).map(s => ({
            'Pregunta': s['Pregunta'],
            'Total Votos': s['Total Votos'],
            'Puntaje Promedio': s['Total Votos'] > 0 ? (s['Suma Puntaje'] / s['Total Votos']).toFixed(2) : 0
        }));

        // Create workbook
        const wb = XLSX.utils.book_new();

        // Add Detailed Worksheet
        const wsDetailed = XLSX.utils.json_to_sheet(dataDetailed);
        XLSX.utils.book_append_sheet(wb, wsDetailed, "Detalle de Respuestas");

        // Add Summary Worksheet
        const wsSummary = XLSX.utils.json_to_sheet(dataSummary);
        XLSX.utils.book_append_sheet(wb, wsSummary, "Resumen por Pregunta");

        // Set column widths for detailed
        wsDetailed['!cols'] = [
            { wch: 20 }, // Fecha
            { wch: 20 }, // ID Anónimo
            { wch: 60 }, // Pregunta
            { wch: 50 }, // Respuesta
            { wch: 15 }, // Puntaje
        ];

        // Set column widths for summary
        wsSummary['!cols'] = [
            { wch: 60 }, // Pregunta
            { wch: 15 }, // Total Votos
            { wch: 20 }, // Promedio
        ];

        // Save file
        XLSX.writeFile(wb, `Reporte_Encuesta_Completo_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <header className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Encuesta de Satisfacción</h1>
                    <p className="text-slate-500">Gestiona las preguntas y visualiza los resultados de la encuesta.</p>
                </div>
                <div className="flex bg-slate-100 p-1 rounded-xl">
                    <button 
                        onClick={() => setActiveTab('questions')}
                        className={`px-6 py-2 rounded-lg font-bold transition-all ${activeTab === 'questions' ? 'bg-white shadow text-blue-600' : 'text-slate-500'}`}
                    >
                        Preguntas
                    </button>
                    <button 
                        onClick={() => setActiveTab('results')}
                        className={`px-6 py-2 rounded-lg font-bold transition-all ${activeTab === 'results' ? 'bg-white shadow text-blue-600' : 'text-slate-500'}`}
                    >
                        Resultados
                    </button>
                </div>
            </header>

            {activeTab === 'questions' ? (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                        <h2 className="font-bold text-slate-700">Listado de Preguntas</h2>
                        <button 
                            onClick={() => openModal()}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-blue-700 transition-all flex items-center gap-2"
                        >
                            <span>+</span> Nueva Pregunta
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 text-slate-400 text-[10px] uppercase tracking-widest font-black">
                                <tr>
                                    <th className="px-6 py-4">Orden</th>
                                    <th className="px-6 py-4">Pregunta</th>
                                    <th className="px-6 py-4">Tipo</th>
                                    <th className="px-6 py-4">Estado</th>
                                    <th className="px-6 py-4 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {questions.map((q) => (
                                    <tr key={q.id} className="hover:bg-slate-50 transition-all">
                                        <td className="px-6 py-4 font-bold text-slate-400">{q.sort_order}</td>
                                        <td className="px-6 py-4 font-bold text-slate-700">{q.text}</td>
                                        <td className="px-6 py-4 text-xs font-black uppercase text-blue-500 bg-blue-50 inline-block mt-4 rounded px-2">{q.type}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded text-[10px] font-black uppercase ${q.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
                                                {q.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button onClick={() => openModal(q)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all">✏️</button>
                                                <button onClick={() => handleDeleteQuestion(q.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all">🗑️</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Quick Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Respuestas</p>
                            <h3 className="text-2xl font-black text-slate-800">{results.length}</h3>
                        </div>
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                            <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-1">Puntaje Promedio General</p>
                            <h3 className="text-2xl font-black text-blue-600">
                                {(results.filter(r => r.rating > 0).reduce((acc, curr) => acc + curr.rating, 0) / (results.filter(r => r.rating > 0).length || 1)).toFixed(2)}
                            </h3>
                        </div>
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                            <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-1">Preguntas Evaluadas</p>
                            <h3 className="text-2xl font-black text-emerald-600">
                                {new Set(results.map(r => r.question_text)).size}
                            </h3>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                            <h2 className="font-bold text-slate-700">Listado Detallado de Respuestas</h2>
                        <button 
                            onClick={handleExportExcel}
                            className="text-xs font-black uppercase text-blue-600 bg-blue-50 px-4 py-2 rounded-lg hover:bg-blue-100 flex items-center gap-2"
                        >
                            <span>📊</span> Exportar Excel
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-slate-50 text-slate-400 text-[10px] uppercase tracking-widest font-black">
                                <tr>
                                    <th className="px-6 py-4">Fecha</th>
                                    <th className="px-6 py-4">Criterio / Pregunta</th>
                                    <th className="px-6 py-4">Respuesta</th>
                                    <th className="px-6 py-4">Puntaje</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {results.map((r) => {
                                    const score = r.rating || '-';
                                    return (
                                        <tr key={r.id} className="hover:bg-slate-50 transition-all text-sm">
                                            <td className="px-6 py-4 text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                                            <td className="px-6 py-4 font-bold text-slate-700">{r.question_text || 'General'}</td>
                                            <td className="px-6 py-4 text-slate-600">{r.answer}</td>
                                            <td className="px-6 py-4">
                                                {score !== '-' ? (
                                                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-lg font-black text-xs">
                                                        {score}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-300">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            )}

            {/* Modal for adding/editing questions */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-fadeIn">
                        <form onSubmit={handleSaveQuestion}>
                            <div className="p-6 border-b border-slate-100">
                                <h3 className="text-xl font-bold text-slate-800">{editingQuestion ? 'Editar Pregunta' : 'Nueva Pregunta'}</h3>
                            </div>
                            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                                <div>
                                    <label className="block text-[10px] font-black uppercase text-slate-400 mb-1 tracking-widest">Texto de la Pregunta</label>
                                    <input 
                                        type="text" 
                                        className="w-full bg-slate-50 border-0 rounded-xl p-3 focus:ring-2 focus:ring-blue-500"
                                        value={formData.text}
                                        onChange={(e) => setFormData({...formData, text: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-400 mb-1 tracking-widest">Tipo de Respuesta</label>
                                        <select 
                                            className="w-full bg-slate-50 border-0 rounded-xl p-3 focus:ring-2 focus:ring-blue-500"
                                            value={formData.type}
                                            onChange={(e) => setFormData({...formData, type: e.target.value})}
                                        >
                                            <option value="text">Texto</option>
                                            <option value="rating_5">Calificación 1-5</option>
                                            <option value="rating_10">Calificación 1-10</option>
                                            <option value="single_choice">Selección Única</option>
                                            <option value="multiple_choice">Selección Múltiple</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-400 mb-1 tracking-widest">Asociar Speaker (Opcional)</label>
                                        <select 
                                            className="w-full bg-slate-50 border-0 rounded-xl p-3 focus:ring-2 focus:ring-blue-500"
                                            value={formData.speaker_id || ''}
                                            onChange={(e) => setFormData({...formData, speaker_id: e.target.value || null})}
                                        >
                                            <option value="">Ninguno / General</option>
                                            {speakers.map(s => (
                                                <option key={s.id} value={s.id}>{s.name} {s.last_name || ''}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-black uppercase text-slate-400 mb-1 tracking-widest">Orden</label>
                                        <input 
                                            type="number" 
                                            className="w-full bg-slate-50 border-0 rounded-xl p-3 focus:ring-2 focus:ring-blue-500"
                                            value={formData.sort_order}
                                            onChange={(e) => setFormData({...formData, sort_order: e.target.value})}
                                        />
                                    </div>
                                    {(formData.type === 'rating_5' || formData.type === 'rating_10') && (
                                        <div className="flex flex-col justify-center">
                                            <label className="flex items-center gap-2 cursor-pointer mt-4">
                                                <input 
                                                    type="checkbox" 
                                                    checked={formData.is_mixed}
                                                    onChange={(e) => setFormData({...formData, is_mixed: e.target.checked})}
                                                    className="w-5 h-5 rounded border-slate-300 text-blue-600"
                                                />
                                                <span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">¿Es Mixta? (Rating + Texto)</span>
                                            </label>
                                        </div>
                                    )}
                                </div>

                                {formData.is_mixed && (formData.type === 'rating_5' || formData.type === 'rating_10') && (
                                    <div className="animate-fadeIn p-4 bg-purple-50 rounded-2xl border border-purple-100">
                                        <label className="block text-[10px] font-black uppercase text-purple-600 mb-1 tracking-widest">Texto de la Sub-pregunta (Texto)</label>
                                        <input 
                                            type="text" 
                                            placeholder="Ej: ¿Por qué dio esta calificación?"
                                            className="w-full bg-white border-0 rounded-xl p-3 focus:ring-2 focus:ring-purple-500"
                                            value={formData.sub_question_text}
                                            onChange={(e) => setFormData({...formData, sub_question_text: e.target.value})}
                                        />
                                    </div>
                                )}

                                {formData.type === 'text' && (
                                    <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
                                        <label className="block text-[10px] font-black uppercase text-emerald-600 mb-1 tracking-widest">Respuesta Correcta (Opcional)</label>
                                        <input 
                                            type="text" 
                                            placeholder="Texto exacto esperado..."
                                            className="w-full bg-white border-0 rounded-xl p-3 focus:ring-2 focus:ring-emerald-500"
                                            value={formData.correct_answer}
                                            onChange={(e) => setFormData({...formData, correct_answer: e.target.value})}
                                        />
                                    </div>
                                )}

                                {(formData.type === 'single_choice' || formData.type === 'multiple_choice') && (
                                    <div className="space-y-2 p-4 bg-blue-50 rounded-2xl border border-blue-100">
                                        <label className="block text-[10px] font-black uppercase text-blue-600 mb-1 tracking-widest">Opciones</label>
                                        <div className="flex gap-2">
                                            <input 
                                                type="text" 
                                                placeholder="Nueva opción..."
                                                className="flex-grow bg-white border-0 rounded-lg p-2 text-sm"
                                                value={newOption}
                                                onChange={(e) => setNewOption(e.target.value)}
                                            />
                                            <button type="button" onClick={addOption} className="bg-blue-600 text-white px-3 py-1 rounded-lg font-bold text-xs">Añadir</button>
                                        </div>
                                        <div className="flex flex-col gap-2 mt-3">
                                            {formData.options.map((opt, i) => (
                                                <div key={i} className="flex items-center justify-between bg-white px-4 py-2 rounded-xl border border-blue-200">
                                                    <div className="flex items-center gap-3">
                                                        <input 
                                                            type="radio" 
                                                            name="correct_survey"
                                                            checked={formData.correct_answer === opt}
                                                            onChange={() => setFormData({...formData, correct_answer: opt})}
                                                            className="w-4 h-4 accent-emerald-500"
                                                        />
                                                        <span className={`text-sm font-bold ${formData.correct_answer === opt ? 'text-emerald-600' : 'text-slate-600'}`}>
                                                            {opt}
                                                        </span>
                                                    </div>
                                                    <button type="button" onClick={() => removeOption(i)} className="text-red-500 hover:text-red-700">✕</button>
                                                </div>
                                            ))}
                                        </div>
                                        <p className="text-[9px] font-black uppercase text-blue-400 mt-2 text-center tracking-widest">Selecciona el círculo para marcar la respuesta correcta</p>
                                    </div>
                                )}

                                <div className="flex items-center gap-4 pt-2">
                                    <label className="flex items-center gap-2 cursor-pointer group">
                                        <input 
                                            type="checkbox" 
                                            checked={formData.required}
                                            onChange={(e) => setFormData({...formData, required: e.target.checked})}
                                            className="w-5 h-5 rounded border-slate-300 text-blue-600"
                                        />
                                        <span className="text-xs font-bold text-slate-600">Obligatoria</span>
                                    </label>
                                    <div className="flex-grow"></div>
                                    <select 
                                        className="bg-slate-50 border-0 rounded-lg p-2 text-xs font-black uppercase"
                                        value={formData.status}
                                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                                    >
                                        <option value="active">Activa</option>
                                        <option value="inactive">Inactiva</option>
                                    </select>
                                </div>
                            </div>
                            <div className="p-6 bg-slate-50 flex gap-3 justify-end">
                                <button 
                                    type="button" 
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-6 py-2 rounded-xl text-slate-500 font-bold hover:bg-slate-200 transition-all"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    className="px-6 py-2 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all"
                                >
                                    Guardar Cambios
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Surveys;
